<script lang="ts" setup>
import { computed, ref } from 'vue';
import { useRoute } from 'vue-router';

import type { ITableColumnsOptions } from '@modules/tables/types';
import type { IAffiliateWebhooksHistoryListItem, IWebhooksRequestDetails } from '@modules/webhooks-history/types';
import VBackButton from '@components/buttons/VBackButton/VBackButton.vue';
import VIconButton from '@components/buttons/VIconButton/VIconButton.vue';
import VCard from '@components/card/VCard/VCard.vue';
import { useI18n } from '@modules/i18n/composables';
import { useCurrentNamespace } from '@modules/permissions/composables';
import VTable from '@modules/tables/components/VTable/VTable.vue';
import { useTableData } from '@modules/tables/composables';
import {
  ADMIN_WEBHOOKS_HISTORY_LIST_ROUTE_NAME,
  AFFILIATE_WEBHOOKS_HISTORY_LIST_ROUTE_NAME,
} from '@modules/webhooks-history/constants';
import {
  TRANSLATION_GROUP_ADMIN_WEBHOOKS_DETAILS,
  TRANSLATION_GROUP_AFFILIATES_WEBHOOKS_DETAILS,
} from '@modules/webhooks-history/submodules/history-details/constants';
import { useWebhooksHistoryDetailsQuery } from '@modules/webhooks-history/submodules/history-details/queries';
import { isEmpty } from '@utils/guards';

import WebhooksHistoryRequestDetailsDialog from '../../components/WebhooksHistoryRequestDetailsDialog/WebhooksHistoryRequestDetailsDialog.vue';

const route = useRoute();
const { isAdminNamespace } = useCurrentNamespace();
const { getTranslation, getTPath } = useI18n();

const isRequestDetailsDialogVisible = ref(false);
const requestHistoryDetails = ref<IWebhooksRequestDetails | undefined>();

const id = computed<number | undefined>(() => (
  route.params.id ? Number(route.params.id) : undefined
));

const title = computed(() => (getTPath(isAdminNamespace.value
  ? TRANSLATION_GROUP_ADMIN_WEBHOOKS_DETAILS
  : TRANSLATION_GROUP_AFFILIATES_WEBHOOKS_DETAILS, 'HEADER_DETAILS')
));

const label = computed(() => (getTranslation(isAdminNamespace.value
  ? TRANSLATION_GROUP_ADMIN_WEBHOOKS_DETAILS
  : TRANSLATION_GROUP_AFFILIATES_WEBHOOKS_DETAILS, 'BACK').value
));

const routeName = computed(() => (isAdminNamespace.value
  ? ADMIN_WEBHOOKS_HISTORY_LIST_ROUTE_NAME
  : AFFILIATE_WEBHOOKS_HISTORY_LIST_ROUTE_NAME
));

const {
  data,
  isLoading,
  isFetching,
  tableState,
  updateTableState,
  refreshTableData,
} = useTableData(useWebhooksHistoryDetailsQuery, id);

const columnOptions: ITableColumnsOptions = {
  kind: {
    defaultMobilePreview: true,
  },
};

function openRequestDetailsDialog({ execution_status, explanations }: IAffiliateWebhooksHistoryListItem) {
  requestHistoryDetails.value = {
    execution_status,
    explanations,
  };
  isRequestDetailsDialogVisible.value = true;
}

function onRequestDetailsDialogHide() {
  requestHistoryDetails.value = undefined;
}
</script>

<template>
  <VCard v-data-qa-auto-id="'WebhooksHistoryDetailsPage'" type="table">
    <VTable
      :data="data"
      :title-t-path="title"
      :loading="isLoading"
      :is-fetching="isFetching"
      :state="tableState"
      :columns-options="columnOptions"
      @state-update="updateTableState"
      @refresh="refreshTableData"
    >
      <template #recordAction="{ listItem }">
        <VIconButton
          v-if="listItem.execution_status && !isEmpty(listItem.explanations)"
          icon="pi pi-eye"
          @click="openRequestDetailsDialog(listItem)"
        />
      </template>
      <template #footerButtons>
        <VBackButton
          :label="label"
          :to="{ name: routeName }"
        />
      </template>
    </VTable>
  </VCard>

  <WebhooksHistoryRequestDetailsDialog
    v-model:visible="isRequestDetailsDialogVisible"
    :data="requestHistoryDetails"
    @after-hide="onRequestDetailsDialogHide"
  />
</template>
